import React, { useEffect, useState } from "react";
import { Bell, ShieldAlert } from "lucide-react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../dataManagement/uiDb/UiDb";
import { setPermission } from "../../../../dataManagement/uiDb/NotificationsDb";
import { useNotificationPermission } from "../../../../hooks/useNotificationPermission";
import "./styles/DineInNotificationGate.css";

/**
 * Blocks entry into the dine-in session UI until push notifications are
 * granted. The whole live-sync pipeline (DINE_IN_* FCM pushes → DineInDb —
 * see dineInPushRouter.ts) depends on it; without a granted permission a
 * diner's screen would silently drift out of sync with the rest of the
 * table (new items, round closes, bill) with no indication anything is
 * wrong. DineIn.tsx renders this instead of any session phase — and holds
 * off calling openTable at all — until state is "granted".
 */
const DineInNotificationGate: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { state, mode, canRequest, ctaLabel, promptAndRegister } =
    useNotificationPermission();
  const [requesting, setRequesting] = useState(false);

  // Web never fires an event when the user flips the site permission from
  // browser chrome (the lock-icon menu) — re-derive on focus so coming back
  // from "Site settings" is picked up without forcing a manual reload.
  useEffect(() => {
    if (mode !== "web" || typeof Notification === "undefined") return;
    const recheck = () => dispatch(setPermission(Notification.permission));
    window.addEventListener("focus", recheck);
    return () => window.removeEventListener("focus", recheck);
  }, [mode, dispatch]);

  const handleEnable = async () => {
    setRequesting(true);
    try {
      await promptAndRegister();
    } finally {
      setRequesting(false);
    }
  };

  // "denied" (web, can't re-prompt) / "blocked" (native) are both dead ends
  // for a JS-triggered dialog — guidance instead of a button that does
  // nothing.
  const isDeadEnd = state === "denied" || state === "blocked";

  return (
    <div className="dine-in-notif-gate">
      <div className="dine-in-notif-gate__icon">
        {isDeadEnd ? <ShieldAlert size={28} /> : <Bell size={28} />}
      </div>
      <h2 className="dine-in-notif-gate__title">
        {isDeadEnd ? "Notifications are turned off" : "Turn on notifications to continue"}
      </h2>
      <p className="dine-in-notif-gate__body">
        {isDeadEnd
          ? "This table's live feed needs notifications to sync — new items, round changes, and the bill all update through them. Enable them in your browser's site settings (the lock icon next to the address bar), then come back to this tab."
          : "This is a shared, live table: everyone's round updates, new items, and the bill sync through push notifications. Turn them on so your screen never falls behind the rest of the table."}
      </p>
      {canRequest ? (
        <button
          className="dine-in-notif-gate__cta"
          onClick={handleEnable}
          disabled={requesting}
        >
          {requesting ? "Requesting..." : ctaLabel}
        </button>
      ) : (
        <p className="dine-in-notif-gate__hint">
          Once enabled, this screen continues on its own — no need to refresh.
        </p>
      )}
    </div>
  );
};

export default DineInNotificationGate;
